import { useMemo, useState, useCallback, useEffect } from "react";
import { Feature, FeatureCollection, LineString, Point, Polygon } from "geojson";
import { LngLatBounds, type MapLayerMouseEvent } from "maplibre-gl";
import { toast } from "react-toastify";
import type { Job } from "../../../models";
import type { JobMapProps, ColorMode, BoundsPadding, EditableJsonKeys } from "./JobMap.types";
import { getJobBoundaryGeometry, generateHatchLines } from "./JobMap.helpers";
import { TileSourceId, TILE_SOURCES, DEFAULT_TILE_SOURCE, TileSourceDef } from "./tileSources";
import { processStandardJobs, processBoundarySync } from "../../../application/map/jobProjection";
import type { LassoMetadata } from "../../../application/map/tools/types";
import { normalizePointsFC } from "../../../domain/sample/sampleService";
import type { SamplePointCollection } from "../../../domain/sample/types";

// Re-export for consumers that imported from here
export { processStandardJobs, processBoundarySync };
export type { ProcessedGeoData, BoundaryModeInput, BoundaryModeResult } from "../../../application/map/jobProjection";

// ── Types ────────────────────────────────────────────────────────────────────

export interface PopupState {
	lng: number;
	lat: number;
	job: Job;
}

export interface JobMapViewModel {
	// Standard layers
	polygonFC: FeatureCollection;
	pointFC: FeatureCollection;
	bounds: LngLatBounds | undefined;

	// Wizard layers (empty FCs when not in wizard mode)
	hatchLinesFC: FeatureCollection;
	nearbyJobsFC: FeatureCollection;
	unmatchedPointsFC: FeatureCollection;
	unmatchedZonesFC: FeatureCollection;
	duplicatePointsFC: FeatureCollection;
	duplicateZonesFC: FeatureCollection;
	highlightFC: FeatureCollection;

	// Popup
	popup: PopupState | null;
	closePopup: () => void;

	// Tile source
	tileSource: TileSourceDef;
	tileSources: TileSourceDef[];
	setTileSourceId: (id: TileSourceId) => void;

	// Display
	effectiveJobs: Job[];
	jobIdKey: string;
	colorMode: ColorMode;
	showToolbar: boolean;
	showEmptyState: boolean;
	hasGeodata: boolean;
	resolvedHeight: string | undefined;
	cooperativeGestures: boolean;
	zoomLevelToDisplayPoints: number;
	boundsPadding: BoundsPadding | undefined;

	// Toolbar
	effectiveLassoMode: boolean;
	effectiveSelectedFeatureIds: Set<string>;
	onToggleLasso: () => void;

	// Click handlers
	onMapClick: (e: MapLayerMouseEvent) => void;
	onPolygonClick: (e: MapLayerMouseEvent) => void;

	// Selection
	selectedJobId: string | null;
	selectedPolygonId: string | null;
	setSelectedJobId: (id: string | null) => void;
	setSelectedPolygonId: (id: string | null) => void;

	// Lasso / edit callbacks
	getLassoMetadata: () => LassoMetadata[];
	onLassoComplete: (selectedIds: string[]) => void;
	getJobData: (jobId: string) => {
		boundary: FeatureCollection | null;
		zones: FeatureCollection | null;
		points: SamplePointCollection | null;
		cores: null;
	};
	onApply: (jobId: string, key: "boundary" | "zones" | "points" | "cores", fc: FeatureCollection) => void;
	onError: (errorMsg: string) => void;

	// Derived display
	displayPolygonFC: FeatureCollection;
	cursor: string | undefined;
}

export interface JobMapViewModelOptions {
	activeTool?: string | null;
}

const EMPTY_FC: FeatureCollection = { type: "FeatureCollection", features: [] };

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useJobMapViewModel(props: JobMapProps, options?: JobMapViewModelOptions): JobMapViewModel {
	const [popup, setPopup] = useState<PopupState | null>(null);
	const [tileSourceId, setTileSourceId] = useState<TileSourceId>("google-satellite");
	const [hatchLinesFC, setHatchLinesFC] = useState<FeatureCollection>(EMPTY_FC);

	// Toolbar state
	const [internalLassoMode, setInternalLassoMode] = useState(false);
	const [internalSelectedFeatureIds, setInternalSelectedFeatureIds] = useState<Set<string>>(new Set());

	const effectiveJobs = useMemo(() => props.jobs ?? (props.job ? [props.job] : []), [props.jobs, props.job]);

	const jobIdKey = useMemo(
		() =>
			effectiveJobs
				.map((j) => j.id)
				.sort()
				.join(","),
		[effectiveJobs],
	);

	const colorMode: ColorMode = props.colorMode ?? "per-job";
	const showToolbar = (props.showToolbar ?? false) || !!props.onGeometryEdit;
	const showEmptyState = props.showEmptyState ?? false;
	const cooperativeGestures = props.gestureHandling === "cooperative";
	const zoomLevelToDisplayPoints = props.zoomLevelToDisplayPoints ?? 10;
	const boundsPadding = props.boundsPadding;

	const isBoundaryMode = colorMode === "boundary-hatch" || colorMode === "boundary-outline";

	const effectiveLassoMode = showToolbar ? internalLassoMode : (props.lassoMode ?? false);
	const effectiveSelectedFeatureIds =
		props.selectedFeatureIds ?? (showToolbar ? internalSelectedFeatureIds : new Set<string>());
	const setEffectiveSelectedFeatureIds = props.onSelectedFeaturesChange ?? setInternalSelectedFeatureIds;

	const resolvedHeight =
		props.height === undefined ? undefined : typeof props.height === "number" ? `${props.height}px` : props.height;

	const hasGeodata = isBoundaryMode
		? effectiveJobs.some((job) => getJobBoundaryGeometry(job) !== null)
		: effectiveJobs.some(
				(job) =>
					(job.files?.boundaryGeoJSON?.features?.length ?? 0) > 0 ||
					(job.files?.pointsGeoJSON?.features?.length ?? 0) > 0 ||
					(job.files?.zonesGeoJSON?.features?.length ?? 0) > 0,
			);

	// Stable string key for Set deps — allows useMemo to detect selection changes
	const selectedKey =
		!effectiveSelectedFeatureIds || effectiveSelectedFeatureIds.size === 0
			? ""
			: Array.from(effectiveSelectedFeatureIds).sort().join(",");

	// ── Standard mode processing ────────────────────────────────────────────
	const standardProcessed = useMemo(() => {
		if (isBoundaryMode)
			return { polygonFeatures: [], pointFeatures: [], bounds: undefined, jobLookup: new Map<string, Job>() };
		return processStandardJobs(effectiveJobs, colorMode, effectiveSelectedFeatureIds);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [effectiveJobs, colorMode, selectedKey, isBoundaryMode]);

	// ── Wizard mode processing (sync part) ──────────────────────────────────
	const wizardProcessed = useMemo(() => {
		if (!isBoundaryMode) return null;
		return processBoundarySync({
			jobs: effectiveJobs,
			colorMode: colorMode as "boundary-hatch" | "boundary-outline",
			hiddenJobIds: props.hiddenJobIds,
			nearbyJobs: props.nearbyJobs,
			hiddenNearbyJobIds: props.hiddenNearbyJobIds,
			unmatchedFeatures: props.unmatchedFeatures,
			hideUnmatchedFeatures: props.hideUnmatchedFeatures,
			duplicateFeatures: props.duplicateFeatures,
			hiddenDuplicateFeatureIds: props.hiddenDuplicateFeatureIds,
			highlightGeometry: props.highlightGeometry,
			selectedFeatureIds: effectiveSelectedFeatureIds,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		effectiveJobs,
		colorMode,
		selectedKey,
		isBoundaryMode,
		props.hiddenJobIds,
		props.nearbyJobs,
		props.hiddenNearbyJobIds,
		props.unmatchedFeatures,
		props.hideUnmatchedFeatures,
		props.duplicateFeatures,
		props.hiddenDuplicateFeatureIds,
		props.highlightGeometry,
	]);

	// ── Async hatch line generation ─────────────────────────────────────────
	useEffect(() => {
		if (!wizardProcessed || wizardProcessed.hatchInputs.length === 0) {
			setHatchLinesFC(EMPTY_FC);
			return;
		}

		let cancelled = false;

		async function generate() {
			const allFeatures: Feature<LineString>[] = [];
			for (const input of wizardProcessed!.hatchInputs) {
				const lines = await generateHatchLines(input.geometry, input.angle, input.spacing);
				if (cancelled) return;
				lines.forEach((coords, _i) => {
					allFeatures.push({
						type: "Feature",
						properties: { color: input.color, strokeWeight: 1.5, strokeOpacity: 0.8 },
						geometry: { type: "LineString", coordinates: coords },
					});
				});
			}
			if (!cancelled) {
				setHatchLinesFC({ type: "FeatureCollection", features: allFeatures });
			}
		}

		generate();
		return () => {
			cancelled = true;
		};
	}, [wizardProcessed]);

	// ── Combine into FCs ────────────────────────────────────────────────────
	const fc = (features: any[]) => ({ type: "FeatureCollection" as const, features });
	const layerData = useMemo(() => {
		if (isBoundaryMode && wizardProcessed) {
			return {
				polygonFC: fc(wizardProcessed.polygonFeatures),
				pointFC: EMPTY_FC,
				nearbyJobsFC: fc(wizardProcessed.nearbyJobFeatures),
				unmatchedPointsFC: fc(wizardProcessed.unmatchedPointFeatures),
				unmatchedZonesFC: fc(wizardProcessed.unmatchedZoneFeatures),
				duplicatePointsFC: fc(wizardProcessed.duplicatePointFeatures),
				duplicateZonesFC: fc(wizardProcessed.duplicateZoneFeatures),
				highlightFC: fc(wizardProcessed.highlightFeatures),
			};
		}
		return {
			polygonFC: fc(standardProcessed.polygonFeatures),
			pointFC: fc(standardProcessed.pointFeatures),
			nearbyJobsFC: EMPTY_FC,
			unmatchedPointsFC: EMPTY_FC,
			unmatchedZonesFC: EMPTY_FC,
			duplicatePointsFC: EMPTY_FC,
			duplicateZonesFC: EMPTY_FC,
			highlightFC: EMPTY_FC,
		};
	}, [isBoundaryMode, wizardProcessed, standardProcessed]);

	const bounds = isBoundaryMode ? wizardProcessed?.bounds : standardProcessed.bounds;
	const jobLookup = isBoundaryMode ? new Map<string, Job>() : standardProcessed.jobLookup;

	// ── Tile source ─────────────────────────────────────────────────────────
	const tileSource = TILE_SOURCES.find((t) => t.id === tileSourceId) || DEFAULT_TILE_SOURCE;

	// ── Handlers ────────────────────────────────────────────────────────────
	const onPolygonClick = useCallback(
		(e: MapLayerMouseEvent) => {
			if (effectiveLassoMode) return;
			const feature = e.features?.[0];
			if (!feature) return;

			const jobId = feature.properties?.jobId;
			const job = jobLookup.get(jobId);
			if (job) {
				setPopup({ lng: e.lngLat.lng, lat: e.lngLat.lat, job });
			}
		},
		[effectiveLassoMode, jobLookup],
	);

	const onMapClick = useCallback(
		(_e: MapLayerMouseEvent) => {
			if (effectiveLassoMode) return;
			setPopup(null);
		},
		[effectiveLassoMode],
	);

	// ── Selection state ────────────────────────────────────────────────
	const [selectedJobId, setSelectedJobId] = useState<string | null>(() =>
		effectiveJobs.length === 1 ? effectiveJobs[0].id : null,
	);
	const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(null);

	// Clear selection when selected job is removed from effective jobs
	useEffect(() => {
		if (selectedJobId && !effectiveJobs.some((j) => j.id === selectedJobId)) {
			setSelectedJobId(effectiveJobs.length === 1 ? effectiveJobs[0].id : null);
			setSelectedPolygonId(null);
		}
	}, [selectedJobId, effectiveJobs]);

	// ── Lasso metadata ─────────────────────────────────────────────────
	const getLassoMetadata = useCallback((): LassoMetadata[] => {
		if (isBoundaryMode) {
			// Wizard mode: build from unmatched FCs
			const metadata: LassoMetadata[] = [];
			const unmatchedPts = layerData.unmatchedPointsFC;
			const unmatchedZns = layerData.unmatchedZonesFC;
			for (const f of unmatchedPts.features) {
				if (f.geometry.type === "Point") {
					metadata.push({
						id: f.properties?.id ?? "",
						coords: [(f.geometry as Point).coordinates],
						featureType: "sample",
					});
				}
			}
			for (const f of unmatchedZns.features) {
				if (f.geometry.type === "Polygon") {
					metadata.push({
						id: f.properties?.id ?? "",
						coords: (f.geometry as Polygon).coordinates[0] ?? [],
						featureType: "zone",
					});
				}
			}
			return metadata;
		}

		// Edit mode: build from selected job's FCs
		if (!selectedJobId) return [];
		const job = effectiveJobs.find((j) => j.id === selectedJobId);
		if (!job) return [];

		const metadata: LassoMetadata[] = [];
		const pointsFC = job.files?.pointsGeoJSON as FeatureCollection | undefined;
		if (pointsFC?.features) {
			for (const f of pointsFC.features) {
				if (f.geometry?.type === "Point") {
					metadata.push({
						id: f.properties?.sample_id ?? f.properties?.id ?? "",
						coords: [(f.geometry as Point).coordinates],
						featureType: "sample",
					});
				}
			}
		}
		return metadata;
	}, [isBoundaryMode, layerData.unmatchedPointsFC, layerData.unmatchedZonesFC, effectiveJobs, selectedJobId]);

	// ── Lasso complete ─────────────────────────────────────────────────
	const handleLassoComplete = useCallback(
		(selectedIds: string[]) => {
			if (props.onLassoComplete) {
				props.onLassoComplete(new Set(selectedIds));
			}
			if (props.onSelectedFeaturesChange) {
				props.onSelectedFeaturesChange(new Set(selectedIds));
			}
		},
		[props.onLassoComplete, props.onSelectedFeaturesChange],
	);

	// ── Job data accessor ──────────────────────────────────────────────
	const getJobData = useCallback(
		(jobId: string) => {
			const job = effectiveJobs.find((j) => j.id === jobId);
			if (!job) return { boundary: null, zones: null, points: null, cores: null };
			const rawPoints = job.files?.pointsGeoJSON as FeatureCollection | undefined;
			return {
				boundary: (job.files?.boundaryGeoJSON as FeatureCollection) ?? null,
				zones: (job.files?.zonesGeoJSON as FeatureCollection) ?? null,
				points: rawPoints ? normalizePointsFC(rawPoints) : null,
				cores: null,
			};
		},
		[effectiveJobs],
	);

	// ── onApply ────────────────────────────────────────────────────────
	const onApply = useCallback(
		(jobId: string, key: "boundary" | "zones" | "points" | "cores", fc: FeatureCollection) => {
			if (!props.onGeometryEdit) return;
			const keyMap: Record<string, EditableJsonKeys> = {
				boundary: "boundaryGeoJSON",
				zones: "zonesGeoJSON",
				points: "pointsGeoJSON",
			};
			const geoKey = keyMap[key];
			if (geoKey) props.onGeometryEdit(jobId, geoKey, fc);
		},
		[props.onGeometryEdit],
	);

	// ── onError ────────────────────────────────────────────────────────
	const onError = useCallback((errorMsg: string) => {
		toast.warning(errorMsg);
	}, []);

	// ── Display polygon FC (highlight selected, filter when editing) ────
	const activeTool = options?.activeTool ?? null;
	const displayPolygonFC = useMemo<FeatureCollection>(() => {
		const isEditing = activeTool === "edit-vertices";
		return {
			type: "FeatureCollection",
			features: layerData.polygonFC.features
				.filter((f) => !(isEditing && f.properties?.id === selectedPolygonId))
				.map((f) => {
					if (f.properties?.id !== selectedPolygonId) return f;
					return {
						...f,
						properties: {
							...f.properties,
							strokeWeight: 4,
							fillOpacity: 0.55,
							strokeOpacity: 1,
						},
					};
				}),
		};
	}, [layerData.polygonFC, selectedPolygonId, activeTool]);

	// ── Cursor ──────────────────────────────────────────────────────────
	const cursor = effectiveLassoMode ? "crosshair" : activeTool ? "crosshair" : undefined;

	const closePopup = () => setPopup(null);

	const handleToggleLasso = useCallback(() => {
		if (effectiveSelectedFeatureIds.size > 0) {
			setEffectiveSelectedFeatureIds(new Set());
		} else {
			setInternalLassoMode((prev) => !prev);
		}
	}, [effectiveSelectedFeatureIds.size, setEffectiveSelectedFeatureIds]);

	return {
		...layerData,
		bounds,
		hatchLinesFC,
		popup,
		closePopup,
		tileSource,
		tileSources: TILE_SOURCES,
		setTileSourceId,
		effectiveJobs,
		jobIdKey,
		colorMode,
		showToolbar,
		showEmptyState,
		hasGeodata,
		resolvedHeight,
		cooperativeGestures,
		zoomLevelToDisplayPoints,
		boundsPadding,
		effectiveLassoMode,
		effectiveSelectedFeatureIds,
		onToggleLasso: handleToggleLasso,
		onMapClick,
		onPolygonClick,
		selectedJobId,
		selectedPolygonId,
		setSelectedJobId,
		setSelectedPolygonId,
		getLassoMetadata,
		onLassoComplete: handleLassoComplete,
		getJobData,
		onApply,
		onError,
		displayPolygonFC,
		cursor,
	};
}
